import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { LineItemsEditor, emptyLine, type LineDraft } from '../components/LineItemsEditor';
import {
  useClients,
  useCreateQuotation,
  useQuotationAction,
  useQuotations,
} from '../hooks/useOps';
import type { Quotation } from '../api/types';
import { formatDate, formatIDR, humanizeStatus, statusColor } from '../lib/format';

export function QuotationsPage() {
  const { data, isLoading, error } = useQuotations();
  const { data: clients } = useClients();
  const createQuotation = useCreateQuotation();
  const action = useQuotationAction();

  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  const clientName = useMemo(() => {
    const map = new Map((clients ?? []).map((c) => [c.id, c.name]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [clients]);

  const openCreate = () => {
    setClientId('');
    setIssueDate(new Date().toISOString().slice(0, 10));
    setValidUntil('');
    setLines([emptyLine()]);
    setFormError(null);
    setOpen(true);
  };

  const submit = async () => {
    try {
      await createQuotation.mutateAsync({
        clientId,
        issueDate,
        validUntil: validUntil || undefined,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
        })),
      });
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const run = async (id: string, act: 'send' | 'accept' | 'convert') => {
    try {
      await action.mutateAsync({ id, action: act });
    } catch (e) {
      window.alert((e as Error).message);
    }
  };

  const columns: GridColDef<Quotation>[] = [
    { field: 'quoteNumber', headerName: 'Quote', minWidth: 140 },
    {
      field: 'clientId',
      headerName: 'Client',
      flex: 1,
      minWidth: 160,
      valueGetter: (v) => clientName(v as string),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => (
        <Chip size="small" label={humanizeStatus(p.value)} color={statusColor(p.value)} variant="outlined" />
      ),
    },
    { field: 'issueDate', headerName: 'Issued', width: 120, valueGetter: (v) => formatDate(v as string) },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (v) => formatIDR(v as string),
    },
    {
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={1}>
          {p.row.status === 'draft' && (
            <Button size="small" onClick={() => run(p.row.id, 'send')}>
              Send
            </Button>
          )}
          {p.row.status === 'sent' && (
            <Button size="small" onClick={() => run(p.row.id, 'accept')}>
              Mark accepted
            </Button>
          )}
          {p.row.status === 'accepted' && !p.row.convertedProjectId && (
            <Button size="small" onClick={() => run(p.row.id, 'convert')}>
              Convert to project
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Quotations"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New quotation
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ height: 600 }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>New quotation</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Stack direction="row" gap={2}>
              <TextField
                select
                label="Client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                sx={{ flex: 1 }}
                required
              >
                {(clients ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Issue date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Valid until"
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <LineItemsEditor lines={lines} onChange={setLines} />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!clientId || lines.some((l) => !l.description) || createQuotation.isPending}
          >
            Create draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
